import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ProfileService } from '../service/profile.service';
import { User } from '../model/user.model';
import { useRouteStore } from '../../../../../core/store/route.store';

interface ProfileContextProps {
  profile: User | null;
  setProfile: (user: User) => void;
  isAdmin: boolean;
  companyId: string | null;
  companyName: string | null;
}

const ProfileContext = createContext<ProfileContextProps>({
  profile: null,
  setProfile: () => {},
  isAdmin: false,
  companyId: null,
  companyName: null,
});

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const { setRoutePrefix } = useRouteStore();

  const updateProfile = useCallback((newProfile: User) => {
    setProfile(newProfile);
    setIsAdmin(newProfile.admin || false);
    setCompanyId(newProfile.companyId);
    setCompanyName(newProfile.companyName);
    localStorage.setItem('userProfile', JSON.stringify(newProfile));
    setRoutePrefix(newProfile.companyName);
  }, [setRoutePrefix]);

  useEffect(() => {
    const cached = localStorage.getItem('userProfile');

    if (cached && cached !== 'undefined' && cached !== 'null') {
      try {
        const profile = JSON.parse(cached);
        updateProfile(profile);
        return;
      } catch (e) {
        localStorage.removeItem('userProfile');
      }
    }

    const getProfile = async () => {
      try {
        const profile = await ProfileService.Get();
        updateProfile(profile);
      } catch (error) {
        console.warn('Failed to fetch profile:', error);
      }
    };

    void getProfile();
  }, [updateProfile]);

  return (
    <ProfileContext.Provider value={{ profile, setProfile: updateProfile, isAdmin, companyId, companyName }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => useContext(ProfileContext);
